/**
 * LockedPlanDialog.tsx
 * 2026-07-10 silent-duplicate fix — explicit choice when a source's only plan
 * is locked (Submitted/"Scheduled").
 *
 * The old resolve flow only matched EDITABLE drafts: visiting "Edit Schedule"
 * on a quote whose plan was already Submitted silently created a brand-new
 * draft and auto-placed every booking (real case: SP-089 Submitted → visit →
 * SP-091 born with 9 duplicate bookings). This dialog replaces that silent
 * create with three explicit actions:
 *
 *   - Open plan       → view the locked plan (the scenario page renders
 *                       Submitted plans read-only)
 *   - Reopen to Edit  → existing reopenScenario (Submitted → Draft,
 *                       un-commits its rows), then open it editable
 *   - Create new draft→ the old behaviour, now an explicit choice
 *                       (resolve with forceCreate)
 *
 * Purely presentational — the async work lives in the callers
 * (ScenarioPlanningPage deep-link path, ScenarioSourcePicker). While `busy`
 * is set the actions are disabled and the busy action shows a spinner; the
 * CALLER closes the dialog when its action completes (unlike ConfirmDialog,
 * which closes on click — here a failed action must keep the choice open).
 *
 * Follows the shared ConfirmDialog look (rounded paper, warning-style title,
 * textTransform none). Colors via palette/theme tokens — no hex literals.
 */

import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import type { Scenario } from '../../../../services/scheduleScenario';
import { sourceLines } from './scenarioSourceLabel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Which action is currently running (disables the others, spins itself). */
export type LockedPlanBusyAction = 'open' | 'reopen' | 'create' | null;

interface LockedPlanDialogProps {
  open: boolean;
  /** The locked (Submitted) plan the source already has. */
  scenario: Scenario | null;
  /** In-flight action, or null when idle. */
  busy: LockedPlanBusyAction;
  /** View the locked plan (read-only). */
  onOpenPlan: () => void;
  /** Reopen (Submitted → Draft) and open it editable. */
  onReopen: () => void;
  /** Explicitly create a separate new draft (old behaviour). */
  onCreateNew: () => void;
  /** Cancel / dismiss without doing anything. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LockedPlanDialog: React.FC<LockedPlanDialogProps> = ({
  open,
  scenario,
  busy,
  onOpenPlan,
  onReopen,
  onCreateNew,
  onClose,
}) => {
  const planLabel = scenario ? `${scenario.Code} — ${scenario.Name}` : '';

  // Name the JOB, not just the plan: legacy plans are auto-named "Draft
  // SP-xxx", so the joined source identity ("Terrane — Q-2026-9065") is what
  // tells the user which work this is. Omitted when the Name already says it
  // (new plans default to the source display name) or the fields are absent.
  const src = scenario ? sourceLines(scenario) : null;
  const jobLabel =
    src && src.primary !== '—' && scenario && !scenario.Name.includes(src.primary)
      ? src.primary
      : null;

  const spinnerOr = (action: LockedPlanBusyAction, icon?: React.ReactNode) =>
    busy === action ? <CircularProgress size={14} color="inherit" /> : icon;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, pb: 0 }}>
        <LockOutlinedIcon color="warning" />
        This job&apos;s plan is locked (Scheduled)
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The plan <b>{planLabel}</b>
          {jobLabel ? <> for <b>{jobLabel}</b></> : null} is already scheduled
          and locked against edits. You can open it read-only, reopen it to
          edit (its bookings return to tentative), or create a separate new
          draft — a new draft books its own machine time on top of the
          scheduled plan.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        <Button onClick={onClose} disabled={busy !== null} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={onCreateNew}
          disabled={busy !== null}
          startIcon={spinnerOr('create')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Create new draft
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={onReopen}
          disabled={busy !== null}
          startIcon={spinnerOr('reopen')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Reopen to Edit
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onOpenPlan}
          disabled={busy !== null}
          startIcon={spinnerOr('open')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Open plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};
